/**
 * COL-621: mark a paid rent row's shortfall as a kickback from the payouts
 * popup — the fallback for a rent an admin lowered by hand without saying
 * coliviq pays the difference (src/lib/rent-kickback.ts).
 *
 * One write: `gross_amount_cents` on the row. What the tenant was charged
 * (`amount_cents`) is untouched, so nothing changes on the tenant's side. The
 * payments trigger refreshes the month's open offset invoice, whose fee then
 * counts the full rent, and the owners list is refetched so the popup's
 * payout reads the new figure.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { assertKickbackMonthOpen } from "@/lib/rent-kickback-guard";

export type MarkRentKickbackInput = {
  paymentId: string;
  bookingId: string;
  /** The amount the popup saw on the row; the write refuses if it moved. */
  amountCents: number;
  grossCents: number;
};

/** Thrown when the row changed since the popup read it; nothing was written. */
export class RentKickbackStaleError extends Error {
  constructor() {
    super("rent-kickback-stale");
    this.name = "RentKickbackStaleError";
  }
}

export async function markRentKickback(
  input: MarkRentKickbackInput
): Promise<void> {
  // The popup hides the action on a closed month, but it may have been open
  // while the invoice went out: re-check against the invoices as they are now.
  const { data: row, error: rowError } = await supabase
    .from("payments")
    .select("due_date")
    .eq("id", input.paymentId)
    .maybeSingle();
  if (rowError) throw rowError;
  if (!row?.due_date) throw new RentKickbackStaleError();
  await assertKickbackMonthOpen(input.bookingId, row.due_date);

  // Guarded on what the popup saw: the same amount, and no gross figure yet.
  // A row edited (or already marked) in the meantime matches nothing, and
  // supabase-js calls a zero-row update success — so the count is checked.
  const { data, error } = await supabase
    .from("payments")
    .update({ gross_amount_cents: input.grossCents })
    .eq("id", input.paymentId)
    .eq("amount_cents", input.amountCents)
    .is("gross_amount_cents", null)
    .select("id");
  if (error) throw error;
  if (!data?.length) throw new RentKickbackStaleError();

  // On the tenant's timeline, as the rent edit it is. Best-effort: a failed
  // audit write must not read as a failed kickback.
  try {
    const { data: auth } = await supabase.auth.getUser();
    const { error: auditError } = await supabase.from("audit_logs").insert({
      user_id: auth?.user?.id ?? null,
      action: "payment_edited",
      resource_type: "payment",
      resource_id: input.paymentId,
      old_values: { gross_amount_cents: null },
      new_values: { gross_amount_cents: input.grossCents },
      metadata: {
        payment_type: "rent",
        kickback: true,
        booking_id: Number(input.bookingId) || input.bookingId,
        source: "tenant_profile",
        via: "owner_payouts",
      },
    });
    if (auditError)
      console.warn("rent kickback audit failed (ignored)", auditError);
  } catch (e) {
    console.warn("rent kickback audit failed (ignored)", e);
  }
}

export function useMarkRentKickback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markRentKickback,
    onSettled: () => invalidateOwnerCaches(qc),
  });
}
